import { Fragment, useRef, useState, useEffect } from 'react';
import key from 'weak-key';
import { Container, Col, Row, Carousel } from '@geberit/gdds';

// components
import { Headline, Formats, getFontWeight } from 'components/ContentElements/Headline';
import { InnerHtml } from 'components/InnerHtml/inner-html';

// styles
import styles from './chart-teaser.module.scss';
import tabStyles from '../Tabs/tabs.module.scss';

// utils
import { CHART_COMPONENTS } from './charts';
import { useIsMobile } from 'components/App/SizeProvider';
import { buildSize, gridSizes } from 'utils/gridSize';
import { classNameBuilder } from 'utils/classNameBuilder';
import hasSomeParentTheClass from 'utils/hasSomeParentTheClass';
import { useThemeName } from 'utils/hooks/use-theme';
import { useSectionId } from 'utils/hooks/use-section-id';

export type ChartTeaserProps = {
  emphasize: string;
  title?: string;
  subtitle?: string;
  contentIndex?: number;
  backgroundColor?: string;
  charts?: {
    width: string;
    labels: string[];
    datasets: {
      data: unknown[];
    }[];
    type: string;
    description: string;
    value: number | string;
    title: string;
    footnotes: string;
  }[];
  spacing?: {
    top?: string;
    bottom?: string;
  };
  position?: string;
};

export function ChartTeaser({
  subtitle,
  contentIndex,
  emphasize,
  backgroundColor,
  charts,
  spacing,
  position,
  ...props
}: Readonly<ChartTeaserProps>) {
  const isMobile = useIsMobile();
  const { title: sectionTitle } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [withoutMargin, setWithoutMargin] = useState('');
  const themeName = useThemeName();
  const sectionId = useSectionId(sectionTitle ?? '', '', false, contentIndex);

  useEffect(() => {
    const parentIsTab = hasSomeParentTheClass(ref?.current, tabStyles.tabs, true);
    if (parentIsTab) setWithoutMargin(styles.withoutMargin);
  }, []);

  const renderWrapper = (children) => {
    if (isMobile && charts && charts.length > 1) {
      return (
        <Col>
          <Carousel
            supportMouse
            responsiveLayout={{
              large: {
                slides: 0,
                slidesToMove: 1,
              },
              medium: {
                slides: 0,
                slidesToMove: 1,
              },
              small: {
                slides: 1,
                slidesToMove: 1,
              },
            }}
            hideButtons
          >
            {children}
          </Carousel>
        </Col>
      );
    }

    return children;
  };

  return (
    <div
      className={classNameBuilder(
        styles.sectionWrapper,
        backgroundColor && styles[backgroundColor],
        spacing?.top === 'small' && styles.marginTopSmall,
        spacing?.bottom === 'small' && styles.marginBottomSmall,
        withoutMargin,
      )}
      id={sectionId}
      ref={ref}
    >
      <Container maxContentWidth={buildSize(gridSizes[position === 'full' ? 'twoThird' : 'full'])}>
        {(sectionTitle || subtitle) && (
          <Row>
            <Col>
              <Headline
                title={sectionTitle}
                subtitle={subtitle}
                tag={Formats.h2}
                format={Formats.h1}
                titleFontWeight={getFontWeight(emphasize === 'title', themeName, Formats.h1)}
                subtitleFontWeight={getFontWeight(emphasize === 'subtitle', themeName, Formats.h1)}
              />
            </Col>
          </Row>
        )}
        <Row>
          {renderWrapper(
            charts?.map((chart) => {
              const {
                width,
                labels,
                datasets,
                type,
                description,
                value,
                title,
                footnotes,
                ...rest
              } = chart;
              const colWidth = width === '50%' ? [4, 4, 6] : [4, 8, 12];
              const figureColWidth = [4, 3, 5];
              const appliedWidth = type === 'chart_key_figure' ? figureColWidth : colWidth;

              const Wrapper = isMobile ? 'div' : Col;
              const Component = CHART_COMPONENTS[type];

              if (!Component) return null;

              return (
                <Fragment key={key(chart)}>
                  {type === 'chart_key_figure' && (
                    <Col className={styles.extraCol} size={[0, 0, 1]}></Col>
                  )}
                  <Wrapper
                    className={styles.chartWrapper}
                    {...(!isMobile ? { size: appliedWidth } : {})}
                    key={key(rest)}
                  >
                    <Component
                      data={{ labels, datasets }}
                      background="white"
                      width="100%"
                      description={description}
                      value={value}
                      title={title}
                      sparkline
                      alignment="vertical"
                      {...rest}
                      showGridY
                      showGridY1
                      showLegend={
                        datasets?.length > 1 ||
                        (type === 'chart_doughnut' && datasets[0].data.length > 1)
                      }
                      tooltip={{
                        mode: 'nearest',
                        position: 'nearest',
                        xAlign: 'center',
                        yAlign: 'bottom',
                      }}
                    />
                    {footnotes && (
                      <InnerHtml as="span" className={styles.footnotes} content={footnotes} />
                    )}
                  </Wrapper>
                </Fragment>
              );
            }),
          )}
        </Row>
      </Container>
    </div>
  );
}
